import React, { useState, useEffect } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Smile, Frown, Meh, Activity, Download, Zap, Loader } from 'lucide-react';

// ✅ Import from your config file
import { REACT_APP_API_URL } from 'config/api-config';

// ✅ Extract base URL (removes trailing slash)
const API_BASE_URL = REACT_APP_API_URL.endsWith('/') ? REACT_APP_API_URL.slice(0, -1) : REACT_APP_API_URL;
export default function StudentBehaviorAnalytics() {
  // ========== STATE DECLARATIONS ==========
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingEmotions, setGeneratingEmotions] = useState(false);
  const [authToken, setAuthToken] = useState(null);

  const [emotionTrendData, setEmotionTrendData] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [stats, setStats] = useState([]);
  const [insights, setInsights] = useState([]);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [studentEmotions, setStudentEmotions] = useState([]);

  // Filter options from API
  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    min_date: new Date().toISOString().split('T')[0],
    max_date: new Date().toISOString().split('T')[0]
  });

  // Filter states for emotion generation
const [emotionFilters, setEmotionFilters] = useState({
  location: "",
  startDate: "",
  endDate: ""
});

  // ========== FETCH FILTER OPTIONS ==========
  const fetchFilterOptions = async () => {
    try {
      console.log("Fetching filter options...");
      
      const response = await fetch(`${API_BASE_URL}/reports/emotion-filter-options`);
      
      console.log("Filter options response status:", response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Filter options error:", errorData);
        throw new Error(`HTTP ${response.status}: ${errorData.detail || 'Failed to fetch locations'}`);
      }
      
      const data = await response.json();
      console.log("Raw filter options data:", data);
      
      // Ensure locations is an array
      const locationData = {
        locations: (data.locations && Array.isArray(data.locations)) ? data.locations : [],
        recent_dates: (data.recent_dates && Array.isArray(data.recent_dates)) ? data.recent_dates : [],
        min_date: data.min_date || new Date().toISOString().split('T')[0],
        max_date: data.max_date || new Date().toISOString().split('T')[0]
      };
      
      console.log("Processed location data:", locationData);
      setFilterOptions(locationData);
      setError(null);
      
      // Set initial location to first available location
      if (locationData.locations && locationData.locations.length > 0) {
        console.log("Setting initial location to:", locationData.locations[0]);
        setEmotionFilters(prev => ({
          ...prev,
          location: locationData.locations[0]
        }));
      } else {
        console.warn("No locations available");
        setError("No locations found in database");
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
      setError(`Failed to load locations: ${err.message}`);
    }
  };

  // ========== FETCH SESSIONS ==========
  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log("Fetching sessions...");
      const response = await fetch(`${API_BASE_URL}/reports/filter_data`);
      
      console.log("Sessions response status:", response.status);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Sessions data:", data);
      
      if (data && data.location && Array.isArray(data.location)) {
        const mockSessions = data.location.map((loc, idx) => ({
          session_id: idx + 1,
          course_name: `Session ${idx + 1}`,
          location: loc,
          start_time: "09:00 AM"
        }));
        setSessions(mockSessions);
        console.log("Set sessions:", mockSessions);
        
        if (mockSessions.length > 0) {
          setSelectedSession(mockSessions[0].session_id);
        }
      } else {
        console.warn("No location data in response");
      }
    } catch (err) {
      console.error("Error fetching sessions:", err);
      setError(`Failed to load sessions: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ========== INITIALIZE ==========
  useEffect(() => {
    // Fetch data immediately without waiting for token
    console.log("Component mounted, fetching data...");
    console.log("API Base URL:", API_BASE_URL);
    fetchSessions();
    fetchFilterOptions();
  }, []);

  // ========== GENERATE RANDOM EMOTIONS ==========
// Fix the generateRandomEmotions function - update stats calculation

const generateRandomEmotions = async () => {
  if (!emotionFilters.location) {
    alert("Please select a location");
    return;
  }

  try {
    setGeneratingEmotions(true);
    setError(null);

    const payload = {
      location: emotionFilters.location
    };

    console.log("Sending payload:", payload);

    const response = await fetch(`${API_BASE_URL}/reports/generate-random-emotions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    console.log("Response status:", response.status);

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Failed to generate emotions');
    }
    
    console.log("Success:", data);
    
    // Update session details
    setSessionDetails({
      session_id: data.session_id,
      course_name: data.course_name,
      location: data.location,
      date: data.date,
      start_time: data.start_time,
      end_time: data.end_time,
      total_students: data.total_students
    });

    // Set student emotions
    setStudentEmotions(data.student_emotions || []);

    // Build aggregated stats - FIX: Calculate engagement correctly
    const emotions = data.generated_emotions;
    const totalStudents = data.total_students;
    
    // Calculate engagement: students not showing sadness
    const engagedStudents = Math.max(0, totalStudents - (emotions.sad || 0));
    const engagementPercent = totalStudents > 0 ? Math.round((engagedStudents / totalStudents) * 100) : 0;
    
    const statsArray = [
      {
        label: "Happy Students",
        value: `${emotions.happy || 0}`,
        icon: Smile,
        color: "green",
        trend: "+5%"
      },
      {
        label: "Need Attention",
        value: `${emotions.sad || 0}`,
        icon: Frown,
        color: "red",
        trend: "-3%"
      },
      {
        label: "Neutral Mood",
        value: `${emotions.neutral || 0}`,
        icon: Meh,
        color: "yellow",
        trend: "+2%"
      },
      {
        label: "Avg Engagement",
        value: `${engagementPercent}%`,
        icon: Activity,
        color: "blue",
        trend: "+8%"
      }
    ];

    setStats(statsArray);

    // Build emotion distribution pie chart
    const emotionChartData = Object.entries(emotions).map(([emotion, count]) => ({
      name: emotion.charAt(0).toUpperCase() + emotion.slice(1),
      value: count,
      color: {
        happy: '#10b981',
        sad: '#ef4444',
        neutral: '#f59e0b',
        anger: '#dc2626',
        fear: '#8b5cf6',
        disgust: '#06b6d4',
        surprise: '#f97316',
        contempt: '#6366f1'
      }[emotion] || '#999'
    }));
    setActivityData(emotionChartData);

    // Build emotion trend
    const trendData = [
      { time: 'Start', happy: emotions.happy, sad: emotions.sad, neutral: emotions.neutral },
      { time: 'Mid', happy: Math.max(0, emotions.happy - 2), sad: emotions.sad + 1, neutral: emotions.neutral },
      { time: 'End', happy: Math.max(0, emotions.happy - 1), sad: emotions.sad + 2, neutral: emotions.neutral }
    ];
    setEmotionTrendData(trendData);

    // Build insights
    const insightsArray = [
      {
        title: "✨ Positive Trend",
        description: emotions.happy >= 1 ? `${emotions.happy} students showing happiness.` : "No students showing happiness."
      },
      {
        title: "📊 Engagement Overview",
        description: engagementPercent >= 50 ? `${engagementPercent}% of students are engaged.` : `Only ${engagementPercent}% of students are engaged - needs improvement.`
      },
      {
        title: "🎯 Attention Needed",
        description: emotions.sad > 0 ? `${emotions.sad} students need attention.` : "No students in distress."
      }
    ];
    setInsights(insightsArray);

    alert(`✅ Report Generated emotions for ${data.total_students} students!`);
  } catch (err) {
    console.error("Error generating emotions:", err);
    setError(err.message || "Failed to generate emotions");
    alert("❌ " + err.message);
  } finally {
    setGeneratingEmotions(false);
  }
};
  // ========== GENERATE BATCH EMOTIONS ==========
const generateBatchEmotions = async () => {
    if (!emotionFilters.location) {
      alert("Please select a location");
      return;
    }

    try {
      setGeneratingEmotions(true);
      setError(null);

      // ✅ FIXED: Use snake_case
      const payload = {
        location: emotionFilters.location,
        start_date: emotionFilters.startDate,  // ✅ Changed from startDate
        end_date: emotionFilters.endDate        // ✅ Changed from endDate
      };

      console.log("Batch payload:", payload);  // Debug log

      const response = await fetch(`${API_BASE_URL}/reports/generate-emotions-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate emotions');
      }

      alert(`✅ Report Generated emotions for ${data.sessions_processed} sessions!`);
    } catch (err) {
      console.error("Error in batch generation:", err);
      setError(err.message);
      alert("❌ " + err.message);
    } finally {
      setGeneratingEmotions(false);
    }
  };
  // ========== EXPORT REPORT ==========
  const exportReport = () => {
    const report = {
      session_id: selectedSession,
      session_details: sessionDetails,
      timestamp: new Date().toISOString(),
      stats: {
        happy: parseInt(stats[0]?.value) || 0,
        needAttention: parseInt(stats[1]?.value) || 0,
        neutral: parseInt(stats[2]?.value) || 0,
        avgEngagement: parseInt(stats[3]?.value) || 0
      },
      student_emotions: studentEmotions,
      emotion_distribution: activityData
    };

    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(report, null, 2))
    );
    element.setAttribute("download", `behavior-report-${selectedSession}-${Date.now()}.json`);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // ========== MAIN RENDER ==========
  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh", padding: "32px" }}>
      <div style={{ margin: "0 auto", maxWidth: "1400px" }}>
        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <h1 style={{ fontSize: "32px", fontWeight: "bold", color: "#1e293b", marginBottom: "8px" }}>
            Student Behavior Analytics
          </h1>
          <p style={{ color: "#64748b" }}>Monitor emotional well-being analytics</p>
        </div>

        {/* Emotion Generation Panel */}
        <div style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          padding: "24px",
          marginBottom: "24px",
          border: "2px solid #3b82f6"
        }}>
          <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#1e293b", marginBottom: "16px" }}>
            🎯 Emotions Analysis Section wise
          </h2>
          
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "16px" }}>
            <div>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#475569", marginBottom: "6px" }}>
                Location <span style={{color: "red"}}>*</span>
              </label>
              <select
                value={emotionFilters.location}
                onChange={(e) => setEmotionFilters({ ...emotionFilters, location: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: emotionFilters.location ? "1px solid #cbd5e1" : "2px solid #ef4444",
                  borderRadius: "6px",
                  fontSize: "14px"
                }}
              >
                <option value="">-- Select Location --</option>
                {filterOptions.locations && filterOptions.locations.length > 0 ? (
                  filterOptions.locations.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))
                ) : (
                  <option disabled>Loading locations...</option>
                )}
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#475569", marginBottom: "6px" }}>
                Start Date
              </label>
              <input
                type="date"
                value={emotionFilters.startDate}
                onChange={(e) => setEmotionFilters({ ...emotionFilters, startDate: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "14px"
                }}
              />
            </div>

              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#475569", marginBottom: "6px" }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={emotionFilters.endDate}
                  onChange={(e) => setEmotionFilters({ ...emotionFilters, endDate: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    fontSize: "14px"
                  }}
                />
              </div>
          </div>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button
              onClick={generateRandomEmotions}
              disabled={generatingEmotions}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 20px",
                backgroundColor: generatingEmotions ? "#cbd5e1" : "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: generatingEmotions ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "600"
              }}
            >
              {generatingEmotions ? <Loader size={18} /> : <Zap size={18} />}
              {generatingEmotions ? "Generating..." : "Get for Session"}
            </button>

            <button
              onClick={generateBatchEmotions}
              disabled={generatingEmotions}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 20px",
                backgroundColor: generatingEmotions ? "#cbd5e1" : "#8b5cf6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: generatingEmotions ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "600"
              }}
            >
              {generatingEmotions ? <Loader size={18} /> : <Zap size={18} />}
              {generatingEmotions ? "Generating..." : "Get Batch"}
            </button>

            <button
              onClick={exportReport}
              disabled={studentEmotions.length === 0}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 20px",
                backgroundColor: studentEmotions.length > 0 ? "#ec4899" : "#cbd5e1",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: studentEmotions.length > 0 ? "pointer" : "not-allowed",
                fontSize: "14px",
                fontWeight: "600"
              }}
            >
              <Download size={18} /> Export JSON
            </button>
          </div>

          {error && (
            <div style={{ marginTop: "16px", padding: "12px", backgroundColor: "#fee2e2", border: "1px solid #fecaca", borderRadius: "6px", color: "#991b1b" }}>
              {error}
            </div>
          )}
        </div>

        {/* Stats Cards */}
        {stats && stats.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "32px" }}>
            {stats.map((stat, i) => {
              if (!stat || !stat.icon) return null;
              const Icon = stat.icon;
              const colorMap = {
                green: "#10b981",
                red: "#ef4444",
                yellow: "#f59e0b",
                blue: "#3b82f6"
              };
              return (
                <div
                  key={i}
                  style={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    padding: "24px"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <h3 style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>{stat.label}</h3>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "8px",
                        backgroundColor: colorMap[stat.color],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <Icon color="white" size={24} />
                    </div>
                  </div>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: colorMap[stat.color] }}>
                    {stat.value}
                  </div>
                  <p style={{ color: "#94a3b8", fontSize: "12px", margin: "8px 0 0 0" }}>{stat.trend} from last period</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Charts */}
        {emotionTrendData && emotionTrendData.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "32px" }}>
            <div style={{ backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "24px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#1e293b", marginBottom: "16px" }}>📈 Emotion Trends</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={emotionTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="time" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0" }} />
                  <Legend />
                  <Line type="monotone" dataKey="happy" stroke="#10b981" strokeWidth={2} />
                  <Line type="monotone" dataKey="sad" stroke="#ef4444" strokeWidth={2} />
                  <Line type="monotone" dataKey="neutral" stroke="#f59e0b" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div style={{ backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "24px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#1e293b", marginBottom: "16px" }}>🧠 Emotion Distribution</h2>
              {activityData && activityData.length > 0 && (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={activityData} dataKey="value" outerRadius={80}>
                      {activityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}

        {/* Insights */}
        {insights && insights.length > 0 && (
          <div style={{ marginBottom: "32px" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#1e293b", marginBottom: "16px" }}>Key Insights</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
              {insights.map((item, i) => (
                <div
                  key={i}
                  style={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    padding: "20px"
                  }}
                >
                  <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b", marginBottom: "8px" }}>{item.title}</h3>
                  <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Session Info */}
        {sessionDetails && (
          <div
            style={{
              backgroundColor: "#f1f5f9",
              borderRadius: "8px",
              padding: "20px",
              border: "1px solid #e2e8f0",
              marginBottom: "32px"
            }}
          >
            <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#1e293b", marginBottom: "16px" }}>Session Information</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", fontSize: "14px" }}>
              <div>
                <span style={{ color: "#64748b" }}>Course:</span>
                <p style={{ fontWeight: "600", color: "#1e293b", margin: "4px 0 0 0" }}>{sessionDetails.course_name}</p>
              </div>
              <div>
                <span style={{ color: "#64748b" }}>Location:</span>
                <p style={{ fontWeight: "600", color: "#1e293b", margin: "4px 0 0 0" }}>{sessionDetails.location}</p>
              </div>
              <div>
                <span style={{ color: "#64748b" }}>Date:</span>
                <p style={{ fontWeight: "600", color: "#1e293b", margin: "4px 0 0 0" }}>{sessionDetails.date}</p>
              </div>
              <div>
                <span style={{ color: "#64748b" }}>Students:</span>
                <p style={{ fontWeight: "600", color: "#1e293b", margin: "4px 0 0 0" }}>{sessionDetails.total_students}</p>
              </div>
            </div>
          </div>
        )}

        {/* Student Emotions Table */}
        {studentEmotions && studentEmotions.length > 0 && (
          <div style={{ backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "24px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#1e293b", marginBottom: "16px" }}>Student Emotion Details</h3>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f1f5f9", borderBottom: "2px solid #e2e8f0" }}>
                    <th style={{ padding: "12px", textAlign: "left", color: "#475569", fontWeight: "600" }}>Student Name</th>
                    <th style={{ padding: "12px", textAlign: "center", color: "#475569", fontWeight: "600" }}>Happy</th>
                    <th style={{ padding: "12px", textAlign: "center", color: "#475569", fontWeight: "600" }}>Sad</th>
                    <th style={{ padding: "12px", textAlign: "center", color: "#475569", fontWeight: "600" }}>Neutral</th>
                    <th style={{ padding: "12px", textAlign: "center", color: "#475569", fontWeight: "600" }}>Anger</th>
                    <th style={{ padding: "12px", textAlign: "center", color: "#475569", fontWeight: "600" }}>Fear</th>
                    <th style={{ padding: "12px", textAlign: "center", color: "#475569", fontWeight: "600" }}>Present</th>
                  </tr>
                </thead>
                <tbody>
                  {studentEmotions.slice(0, 10).map((student, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "12px", color: "#1e293b" }}>{student.student_name}</td>
                      <td style={{ padding: "12px", textAlign: "center", color: "#10b981", fontWeight: "600" }}>{student.happy}</td>
                      <td style={{ padding: "12px", textAlign: "center", color: "#ef4444", fontWeight: "600" }}>{student.sad}</td>
                      <td style={{ padding: "12px", textAlign: "center", color: "#f59e0b", fontWeight: "600" }}>{student.neutral}</td>
                      <td style={{ padding: "12px", textAlign: "center", color: "#dc2626", fontWeight: "600" }}>{student.anger}</td>
                      <td style={{ padding: "12px", textAlign: "center", color: "#8b5cf6", fontWeight: "600" }}>{student.fear}</td>
                      <td style={{ padding: "12px", textAlign: "center", color: student.is_present ? "#10b981" : "#ef4444", fontWeight: "600" }}>
                        {student.is_present ? "Yes" : "No"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {studentEmotions.length > 10 && (
              <p style={{ marginTop: "12px", color: "#64748b", fontSize: "13px" }}>
                Showing 10 of {studentEmotions.length} students
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}